import React, { createContext, useContext, useEffect } from 'react';
import { 
  getCurrentBranding, 
  getCSSCustomProperties,
  validateBrandingAssets 
} from '../../utils/brandingUtils';

// Create branding context
const BrandingContext = createContext(null);

/**
 * Branding Provider Component
 * Applies global CSS custom properties and provides branding context
 */
export const BrandingProvider = ({ children }) => {
  const branding = getCurrentBranding();
  const cssProperties = getCSSCustomProperties();

  useEffect(() => {
    // Apply CSS custom properties to the document root
    Object.entries(cssProperties).forEach(([property, value]) => {
      document.documentElement.style.setProperty(property, value);
    });

    // Update document title with branded app name
    document.title = branding.app.title;

    // Update favicon if specified
    const favicon = document.querySelector('link[rel="icon"]');
    if (favicon && branding.images.favicon) {
      favicon.href = branding.images.favicon;
    }

    // Update apple touch icon if specified
    const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (appleTouchIcon && branding.images.appleTouchIcon) {
      appleTouchIcon.href = branding.images.appleTouchIcon;
    }

    // Validate branding assets in development
    if (process.env.NODE_ENV === 'development') {
      validateBrandingAssets().then(result => {
        if (!result.valid) {
          console.warn('Branding validation issues:', result);
        }
      });
    }
  }, [branding, cssProperties]);

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  );
};

/**
 * Hook to access branding context
 * Alternative to useBranding hook for components that prefer context
 */
export const useBrandingContext = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBrandingContext must be used within a BrandingProvider');
  }
  return context;
};

export default BrandingProvider; 